import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { AlertTriangle, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

// Side-effect: register campaigns hooks for all integrations
import "../hooks";
import "../api";

import { campaignIntegrationQueryKeys } from "../api/query-keys";
import { useCampaignIntegrationsInfinite } from "../hooks/useCampaignIntegrationsInfinite";
import { useCampaignsClientDetailsBreadcrumbs } from "../hooks/useCampaignsClientDetailsBreadcrumbs";
import { useCampaignsDataProvider } from "../providers/useCampaignsDataProvider";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/v2/dialog";
import { ROUTES } from "@/constants/routes";
import { useIntegrationModule } from "@/features/integrations/hooks/useIntegrationModule";
import { ModuleDetailPageEmptyState } from "@/features/integrations/shared/components/ModuleDetailPageEmptyState";
import {
  getActionHandlers,
  renderComponent,
} from "@/features/integrations/shared/renderer";
import { useSchemaIntegrationData } from "@/features/integrations/shared/renderer/hooks/useSchemaIntegrationData";
import { useSchemaRowClick } from "@/features/integrations/shared/renderer/hooks/useSchemaRowClick";
import { decodeSlug } from "@/features/integrations/utils/slug";
import { instance } from "@/lib/axios/instance";
import { useAuth } from "@/lib/hooks/useAuth";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import useToast from "@/lib/hooks/useToast";

function CampaignsClientDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { clientId, integrationName, moduleName } = useParams<{
    clientId?: string;
    integrationName?: string;
    moduleName?: string;
  }>();
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();

  const { breadcrumbs } = useCampaignsClientDetailsBreadcrumbs();

  const decodedIntegrationName = integrationName
    ? decodeSlug(integrationName)
    : null;
  const backendIntegrationName = decodedIntegrationName ?? "";

  const { searchValue, setSearchValue } = useSearchSearchParams();
  const debouncedSearch = useDebounce(searchValue, 300);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});

  const moduleQuery = useIntegrationModule({
    integrationName: backendIntegrationName,
    moduleName: moduleName || "",
    tenantId: tenantId ?? undefined,
    clientId: clientId ?? undefined,
  });

  useEffect(() => {
    if (!moduleQuery.isPending) {
      if (
        moduleQuery.isError ||
        moduleQuery.data === null ||
        (moduleQuery.data !== undefined && !moduleQuery.data.isActive)
      ) {
        if (integrationName) {
          const redirectPath = clientId
            ? `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${integrationName}`
            : `/${ROUTES.integrations}/${integrationName}`;
          navigate(redirectPath, { replace: true });
        }
      }
    }
  }, [
    moduleQuery.isPending,
    moduleQuery.isError,
    moduleQuery.data,
    integrationName,
    clientId,
    navigate,
  ]);

  const isModuleActive = moduleQuery.data?.isActive ?? false;
  const provider = useCampaignsDataProvider(backendIntegrationName, clientId);
  const ui = useMemo(() => provider.getClientViews(), [provider]);

  const integrationData = useSchemaIntegrationData(
    backendIntegrationName,
    "campaigns",
    clientId,
    tenantId ?? undefined
  );

  const infiniteQuery = useCampaignIntegrationsInfinite({
    integrationName: backendIntegrationName,
    clientId,
    search: debouncedSearch,
    statusFilter: filterValues?.statusFilter,
  });

  const enrichedItems = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [infiniteQuery.data?.pages],
  );

  const rowClickHandler = useSchemaRowClick(
    backendIntegrationName,
    "campaigns",
    clientId
  );

  const actionHandlers = useMemo(
    () => getActionHandlers(backendIntegrationName),
    [backendIntegrationName]
  );

  const executeAction = useCallback(
    async (
      actionKey: string,
      messages: { successMessage: string; errorMessage: string }
    ) => {
      const handler = actionHandlers[actionKey];
      if (!handler) return;
      try {
        await handler({
          integrationName: backendIntegrationName,
          moduleName: moduleName ?? "campaigns",
          tenantId: tenantId ?? undefined,
          clientId: clientId ?? undefined,
          queryClient: {
            invalidateQueries: (options) =>
              queryClient.invalidateQueries(
                options as Parameters<typeof queryClient.invalidateQueries>[0]
              ),
            resetQueries: (options) =>
              queryClient.resetQueries(
                options as Parameters<typeof queryClient.resetQueries>[0]
              ),
            refetchQueries: async (options) => {
              await queryClient.refetchQueries(
                options as Parameters<typeof queryClient.refetchQueries>[0]
              );
            },
            getQueryData: <T = unknown>(queryKey: readonly unknown[]) =>
              queryClient.getQueryData<T>(queryKey),
            setQueryData: <T = unknown>(
              queryKey: readonly unknown[],
              data: T | undefined
            ) => {
              queryClient.setQueryData<T>(queryKey, data);
            },
            removeQueries: (options) =>
              queryClient.removeQueries(
                options as Parameters<typeof queryClient.removeQueries>[0]
              ),
            cancelQueries: async (options) => {
              await queryClient.cancelQueries(
                options as Parameters<typeof queryClient.cancelQueries>[0]
              );
            },
          },
          onSuccess: () => {
            if (messages.successMessage) {
              showToast(t(messages.successMessage), "success");
            }
          },
          onError: (error) => {
            let errorMessage: string;
            if (error instanceof AxiosError) {
              const problemDetails = error.response?.data as
                | { detail?: string }
                | undefined;
              errorMessage =
                problemDetails?.detail || error.message || String(error);
            } else if (error instanceof Error) {
              errorMessage = error.message;
            } else {
              errorMessage = String(error);
            }
            showToast(errorMessage, "error");
          },
        });
      } catch (error) {
        let errorMessage: string;
        if (error instanceof AxiosError) {
          const problemDetails = error.response?.data as
            | { detail?: string }
            | undefined;
          errorMessage =
            problemDetails?.detail || error.message || String(error);
        } else if (error instanceof Error) {
          errorMessage = error.message;
        } else {
          errorMessage = String(error);
        }
        showToast(errorMessage, "error");
      }
    },
    [
      actionHandlers,
      backendIntegrationName,
      moduleName,
      tenantId,
      clientId,
      showToast,
      t,
      queryClient,
    ]
  );

  const [selectedCampaign, setSelectedCampaign] = useState<Record<string, unknown> | null>(null);
  const ns = `integrations.${backendIntegrationName.toLowerCase()}.campaigns`;

  const handleItemClick = useCallback(
    (item: unknown) => {
      if (rowClickHandler) {
        rowClickHandler(item);
        return;
      }
      const record = item as Record<string, unknown>;
      if (record.syncStatus === "Failed" || record.syncStatus === "NotSynced") {
        setSelectedCampaign(record);
      }
    },
    [rowClickHandler]
  );

  const context = useMemo(
    () => ({
      integrationName: backendIntegrationName,
      moduleName: moduleName ?? "campaigns",
      tenantId: tenantId ?? undefined,
      clientId: clientId ?? undefined,
      executeAction,
      data: integrationData.data,
      items: enrichedItems,
      tableData: enrichedItems,
      isLoading: infiniteQuery.isLoading,
      infiniteScroll: {
        hasMore: infiniteQuery.hasNextPage ?? false,
        fetchMore: () => infiniteQuery.fetchNextPage(),
        isFetchingMore: infiniteQuery.isFetchingNextPage,
      },
      searchValue,
      onSearchChange: setSearchValue,
      filterValues,
      onFilterChange: (key: string, value: unknown) => {
        setFilterValues((prev) => ({ ...prev, [key]: String(value ?? "") }));
      },
      onItemClick: handleItemClick,
      isItemClickable: (item: unknown) => {
        if (rowClickHandler) return true;
        const record = item as Record<string, unknown>;
        return record.syncStatus === "Failed" || record.syncStatus === "NotSynced";
      },
    }),
    [
      backendIntegrationName,
      moduleName,
      tenantId,
      clientId,
      executeAction,
      integrationData.data,
      enrichedItems,
      infiniteQuery,
      searchValue,
      setSearchValue,
      filterValues,
      handleItemClick,
      rowClickHandler,
    ]
  );

  if (moduleQuery.isPending) {
    return null;
  }

  if (moduleQuery.isError || !moduleQuery.data || !isModuleActive) {
    return null;
  }

  return (
    <>
      <ModernPageLayout
        breadcrumbs={breadcrumbs}
        title={t("integrations.modules.names.campaigns")}
        subtitle={t(`integrations.${backendIntegrationName.toLowerCase()}.campaigns.subtitle`, { defaultValue: t("integrations.modules.descriptions.campaigns") })}
      >
        {ui.length > 0 ? (
          <div className="flex flex-col gap-6">
            {ui.map((component, index) => (
              <div key={`${component.component}-${index}`}>
                {renderComponent(component, context)}
              </div>
            ))}
          </div>
        ) : (
          <ModuleDetailPageEmptyState />
        )}
      </ModernPageLayout>

      {selectedCampaign && (
        <Dialog open onOpenChange={(open) => { if (!open) setSelectedCampaign(null); }}>
          <DialogContent showCloseButton={false}>
            <DialogHeader>
              <DialogTitle>
                {t(`${ns}.issueFound`, { defaultValue: "Issue Found" })}
              </DialogTitle>
              <DialogDescription>
                {t(`${ns}.forCampaign`, {
                  defaultValue: "For campaign {{name}}",
                  name: String(selectedCampaign.campaignName ?? ""),
                })}
              </DialogDescription>
            </DialogHeader>

            <CampaignDetails campaign={selectedCampaign} ns={ns} />

            <Alert variant="warning">
              <AlertTriangle className="size-4" />
              <AlertTitle>
                {t(`${ns}.syncFailed`, { defaultValue: "Sync Failed" })}
              </AlertTitle>
              <AlertDescription>
                {selectedCampaign.lastSyncError ? (
                  <div className="flex flex-col gap-1">
                    <code className="block max-h-32 overflow-auto whitespace-pre-wrap rounded-lg bg-muted px-2 py-1 text-xs">
                      {String(selectedCampaign.lastSyncError ?? "")}
                    </code>
                    {typeof selectedCampaign.lastSyncErrorDate === "string" && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(selectedCampaign.lastSyncErrorDate).toLocaleString()}
                      </span>
                    )}
                  </div>
                ) : (
                  String(
                    selectedCampaign.mismatchReason ??
                    t(`${ns}.syncIssue`, { defaultValue: "This campaign has a sync issue." })
                  )
                )}
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedCampaign(null)}>
                <X className="size-4" />
                {t("common.cancel", { defaultValue: "Cancel" })}
              </Button>
              <Button
                onClick={async () => {
                  try {
                    await instance.post(
                      `/api/v2/Integrations/${encodeURIComponent(backendIntegrationName)}/campaigns/overview/repair`,
                      {},
                      { params: { clientId, campaignId: selectedCampaign.campaignId } }
                    );
                    showToast(
                      t(`${ns}.repairSuccess`, { defaultValue: "Campaign sync repair triggered successfully." }),
                      "success"
                    );
                    queryClient.invalidateQueries({
                      queryKey: campaignIntegrationQueryKeys.byModule(backendIntegrationName),
                    });
                  } catch (error) {
                    const msg =
                    error instanceof AxiosError
                      ? (error.response?.data as { detail?: string })?.detail ?? error.message
                      : String(error);
                    showToast(msg, "error");
                  }
                  setSelectedCampaign(null);
                }}
              >
                <RefreshCw className="size-4" />
                {t(`${ns}.repairSync`, { defaultValue: "Repair sync" })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function CampaignDetails({ campaign, ns }: { campaign: Record<string, unknown>; ns: string }) {
  const { t } = useTranslation();

  const falconStatus = campaign.falconStatus ? String(campaign.falconStatus) : null;
  const remoteStatusValue = campaign.remoteStatusValue ? String(campaign.remoteStatusValue) : null;
  const syncStatus = campaign.syncStatus ? String(campaign.syncStatus) : null;

  const normalizeStatus = (status: string | null) => status?.toLowerCase() ?? "inactive";

  const falconLabel = normalizeStatus(falconStatus) === "active" ? t("common.active")
    : normalizeStatus(falconStatus) === "paused" ? t("common.paused")
      : t("common.inactive");
  const platformLabel = syncStatus === "NotSynced"
    ? t(`${ns}.notSynced`, { defaultValue: "Not Synced" })
    : normalizeStatus(remoteStatusValue) === "active" ? t("common.active")
      : normalizeStatus(remoteStatusValue) === "paused" ? t("common.paused")
        : t("common.inactive");

  const currentMonthBudget = campaign.currentMonthBudgetFormatted ? String(campaign.currentMonthBudgetFormatted) : null;
  const remoteBudget = campaign.remoteBudgetFormatted ? String(campaign.remoteBudgetFormatted) : null;

  const statusMismatch = syncStatus === "NotSynced" || falconLabel !== platformLabel;
  const budgetMismatch = currentMonthBudget != null && remoteBudget != null && currentMonthBudget !== remoteBudget;

  const mismatches: { falconLabel: string; falconValue: string; remoteLabel: string; remoteValue: string }[] = [];

  if (statusMismatch) {
    mismatches.push({
      falconLabel: t(`${ns}.falconStatus`, { defaultValue: "Falcon Status" }),
      falconValue: falconLabel,
      remoteLabel: t(`${ns}.remoteStatus`, { defaultValue: "Werkzoeken Status" }),
      remoteValue: platformLabel,
    });
  }

  if (budgetMismatch) {
    mismatches.push({
      falconLabel: t(`${ns}.currentMonthBudget`, { defaultValue: "Current Month Budget" }),
      falconValue: currentMonthBudget,
      remoteLabel: t(`${ns}.remoteBudget`, { defaultValue: "Werkzoeken Budget" }),
      remoteValue: remoteBudget,
    });
  }

  if (mismatches.length === 0) return null;

  return (
    <div className="flex flex-col gap-3 text-sm">
      {mismatches.map((m) => (
        <div key={m.falconLabel} className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">{m.falconLabel}</span>
            <span className="font-medium">{m.falconValue}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">{m.remoteLabel}</span>
            <span className="font-medium">{m.remoteValue}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default CampaignsClientDetailPage;
